/**
 * Lighting preview passes: bloom + contrast/saturation grading, applied in
 * place on the camera's main colour target so everything downstream — the
 * final blit, the stochastic resolve, thumbnails and preview videos read
 * through `render.offscreen` — sees the graded image.
 *
 * Five full-screen quad passes on the editor's own SimpleRenderPass utility
 * (no engine FramePass internals): a quarter-res thresholded downsample, a
 * separable blur, the grade (main + bloom → scratch) and a copy back. All of
 * them are disabled while the grade is neutral, so the default pipeline is
 * untouched. Tonemap and exposure are not here: those go through the engine
 * camera exactly as the viewer applies them.
 */

import {
    ADDRESS_CLAMP_TO_EDGE,
    FILTER_NEAREST,
    PIXELFORMAT_RGBA16F,
    GraphicsDevice,
    RenderPass,
    RenderTarget,
    Texture
} from 'playcanvas';

import { ShaderQuad, SimpleRenderPass } from '../utils/simple-render-pass';

export type Grade = { contrast: number; saturation: number; bloomIntensity: number };

const VS = /* wgsl */`
attribute vertex_position: vec2f;

@vertex
fn vertexMain(input: VertexInput) -> VertexOutput {
    var output: VertexOutput;
    output.position = vec4f(input.vertex_position, 0.0, 1.0);
    return output;
}
`;

const LOAD = /* wgsl */`
var srcTexture: texture_2d<f32>;

fn ld(p: vec2i) -> vec4f {
    let dims = vec2i(textureDimensions(srcTexture));
    return textureLoad(srcTexture, clamp(p, vec2i(0), dims - vec2i(1)), 0);
}
`;

// quarter-res box average with a soft knee: only the bright part blooms
const DOWN_FS = /* wgsl */`
${LOAD}
uniform bloomKnee: vec2f;

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let base = vec2i(pcPosition.xy) * 4;
    var sum = vec3f(0.0);
    for (var j = 0; j < 4; j++) {
        for (var i = 0; i < 4; i++) {
            sum = sum + min(max(ld(base + vec2i(i, j)).rgb, vec3f(0.0)), vec3f(16.0));
        }
    }
    let c = sum / 16.0;
    let l = max(c.r, max(c.g, c.b));
    let k = clamp((l - uniform.bloomKnee.x) * uniform.bloomKnee.y, 0.0, 1.0);
    output.color = vec4f(c * k, 1.0);
    return output;
}
`;

const BLUR_FS = /* wgsl */`
${LOAD}
uniform blurDir: vec2f;

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let p = vec2i(pcPosition.xy);
    let d = vec2i(uniform.blurDir);
    var w = array<f32, 5>(0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216);
    var c = ld(p).rgb * w[0];
    for (var i = 1; i < 5; i++) {
        c = c + (ld(p + d * i).rgb + ld(p - d * i).rgb) * w[i];
    }
    output.color = vec4f(c, 1.0);
    return output;
}
`;

const GRADE_FS = /* wgsl */`
${LOAD}
var bloomTexture: texture_2d<f32>;
// contrast, saturation, bloom intensity, unused
uniform gradeParams: vec4f;

fn lb(p: vec2i) -> vec3f {
    let dims = vec2i(textureDimensions(bloomTexture));
    return textureLoad(bloomTexture, clamp(p, vec2i(0), dims - vec2i(1)), 0).rgb;
}

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let p = vec2i(pcPosition.xy);
    let s = ld(p);
    var rgb = s.rgb;
    if (uniform.gradeParams.z > 0.0) {
        // bilinear tap into the quarter-res bloom
        let bd = vec2f(textureDimensions(bloomTexture));
        let sd = vec2f(textureDimensions(srcTexture));
        let uv = (vec2f(p) + vec2f(0.5)) / sd * bd - vec2f(0.5);
        let b0 = vec2i(floor(uv));
        let f = fract(uv);
        let top = mix(lb(b0), lb(b0 + vec2i(1, 0)), f.x);
        let bottom = mix(lb(b0 + vec2i(0, 1)), lb(b0 + vec2i(1, 1)), f.x);
        rgb = rgb + mix(top, bottom, f.y) * uniform.gradeParams.z;
    }
    rgb = (rgb - vec3f(0.5)) * uniform.gradeParams.x + vec3f(0.5);
    let luma = dot(rgb, vec3f(0.299, 0.587, 0.114));
    rgb = mix(vec3f(luma), rgb, uniform.gradeParams.y);
    // alpha carries the stochastic sentinel: keep it
    output.color = vec4f(max(rgb, vec3f(0.0)), s.a);
    return output;
}
`;

const COPY_FS = /* wgsl */`
${LOAD}

@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    output.color = ld(vec2i(pcPosition.xy));
    return output;
}
`;

class GradePasses {
    passes: RenderPass[];

    private grade: Grade = { contrast: 1, saturation: 1, bloomIntensity: 0 };
    private readonly bloomA: RenderTarget;
    private readonly bloomB: RenderTarget;
    private readonly scratch: RenderTarget;
    private readonly quads: ShaderQuad[] = [];
    private readonly down: SimpleRenderPass;
    private readonly blurH: SimpleRenderPass;
    private readonly blurV: SimpleRenderPass;
    private readonly gradePass: SimpleRenderPass;
    private readonly copy: SimpleRenderPass;

    /**
     * @param mainTarget - the camera's main target (read by the grade)
     * @param colorTarget - the depth-less view of the same colour buffer (written by the copy back)
     */
    constructor(device: GraphicsDevice, mainTarget: RenderTarget, colorTarget: RenderTarget, width: number, height: number) {
        const texture = (name: string, w: number, h: number) => new Texture(device, {
            name,
            width: w,
            height: h,
            format: PIXELFORMAT_RGBA16F,
            mipmaps: false,
            minFilter: FILTER_NEAREST,
            magFilter: FILTER_NEAREST,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        const target = (name: string, w: number, h: number) => new RenderTarget({
            colorBuffer: texture(name, w, h),
            depth: false,
            autoResolve: false
        });
        const qw = Math.max(1, width >> 2);
        const qh = Math.max(1, height >> 2);
        this.bloomA = target('olloBloomA', qw, qh);
        this.bloomB = target('olloBloomB', qw, qh);
        this.scratch = target('olloGradeScratch', width, height);

        const pass = (fs: string, name: string, rt: RenderTarget, vars: () => object) => {
            const quad = new ShaderQuad(device, VS, fs, name);
            this.quads.push(quad);
            const p = new SimpleRenderPass(device, quad, { vars });
            p.name = name;
            p.init(rt);
            p.enabled = false;
            return p;
        };

        this.down = pass(DOWN_FS, 'ollo-bloom-down', this.bloomA, () => ({
            srcTexture: mainTarget.colorBuffer,
            bloomKnee: [0.6, 1 / 0.4]
        }));
        this.blurH = pass(BLUR_FS, 'ollo-bloom-blur-h', this.bloomB, () => ({
            srcTexture: this.bloomA.colorBuffer,
            blurDir: [1, 0]
        }));
        this.blurV = pass(BLUR_FS, 'ollo-bloom-blur-v', this.bloomA, () => ({
            srcTexture: this.bloomB.colorBuffer,
            blurDir: [0, 1]
        }));
        this.gradePass = pass(GRADE_FS, 'ollo-grade', this.scratch, () => ({
            srcTexture: mainTarget.colorBuffer,
            bloomTexture: this.bloomA.colorBuffer,
            gradeParams: [this.grade.contrast, this.grade.saturation, this.grade.bloomIntensity, 0]
        }));
        this.copy = pass(COPY_FS, 'ollo-grade-copy', colorTarget, () => ({
            srcTexture: this.scratch.colorBuffer
        }));

        this.passes = [this.down, this.blurH, this.blurV, this.gradePass, this.copy];
    }

    get active() {
        const g = this.grade;
        return g.contrast !== 1 || g.saturation !== 1 || g.bloomIntensity > 0;
    }

    setGrade(grade: Grade) {
        this.grade = { ...grade };
        const on = this.active;
        const bloom = on && grade.bloomIntensity > 0;
        this.down.enabled = bloom;
        this.blurH.enabled = bloom;
        this.blurV.enabled = bloom;
        this.gradePass.enabled = on;
        this.copy.enabled = on;
    }

    resize(width: number, height: number) {
        const qw = Math.max(1, width >> 2);
        const qh = Math.max(1, height >> 2);
        this.bloomA.resize(qw, qh);
        this.bloomB.resize(qw, qh);
        this.scratch.resize(width, height);
    }

    destroy() {
        this.passes.forEach(p => p.destroy());
        this.quads.forEach(q => q.destroy());
        [this.bloomA, this.bloomB, this.scratch].forEach((rt) => {
            rt.colorBuffer?.destroy();
            rt.destroy();
        });
    }
}

export { GradePasses };
